/**
 * Composant générique pour les modales d'assessment
 * Remplace AssessmentModalAIDigitalTransformation et AssessmentModalAILearningStrategy
 * pour éviter la duplication de code
 */

import React, { useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { AssessmentNavigation } from './AssessmentNavigation';
import { AssessmentModalHeader } from './AssessmentModalHeader';
import { AssessmentModalContent } from './AssessmentModalContent';
import { useGenericAssessment } from '@/hooks/useGenericAssessment';
import { useLocalAssessmentSave } from '@/hooks/useLocalAssessmentSave';
import { useModal } from '@/contexts/ModalContext';
import { useImageProxy } from '@/hooks/useImageProxy';
import { trackAssessmentCompleted } from '@/hooks/useGA4Events';

interface AssessmentModalGenericProps {
  isOpen: boolean;
  onClose: () => void;
  startAtFirstQuestion?: boolean;
  assessmentType: 'ia-diagnostic' | 'training-transformation';
  data: any[];
  categoryMapping: any;
  axisDescriptions?: Record<string, string>;
}

const AssessmentModalGeneric: React.FC<AssessmentModalGenericProps> = ({
  isOpen,
  onClose,
  startAtFirstQuestion = false,
  assessmentType,
  data,
  categoryMapping,
  axisDescriptions
}) => {
  const { setModalOpen } = useModal();
  const assessmentHook = useGenericAssessment({
    isOpen,
    startAtFirstQuestion,
    assessmentType,
    data,
    categoryMapping
  });
  const {
    currentStep,
    answers,
    contactInfo,
    modalContentRef,
    setContactInfo,
    resetModal,
    nextStep,
    forceNextStep,
    prevStep,
    updateAnswer,
    getProgress,
    profileQuestions,
    categories,
    questionsByCategory,
    totalSteps,
    calculateScores,
    canProceed,
    getFilteredQuestionsForCurrentCategory
  } = assessmentHook;
  const { saveToDatabase, isSubmitting } = useLocalAssessmentSave();

  useImageProxy();

  useEffect(() => {
    setModalOpen(isOpen);
    return () => setModalOpen(false);
  }, [isOpen, setModalOpen]);

  const handleConfirmClose = () => {
    resetModal();
    onClose();
  };

  const handleContactChange = (field: string, value: string | boolean) => {
    setContactInfo(prev => {
      const updated = { ...prev } as any;
      if (field === 'role') {
        updated.function = String(value);
      } else {
        updated[field] = value;
      }
      return updated;
    });
  };

  const handleContactSubmit = async (contactData: any) => {
    const submissionId = await saveToDatabase(contactData, assessmentType);
    if (submissionId) {
      // HubSpot is now populated by the native embedded assessment form itself.
      // Do not duplicate the contact through the tracking API.
      setContactInfo(contactData);
      trackAssessmentCompleted(assessmentType);
      await forceNextStep();
    } else {
      console.error(`[${assessmentType.toUpperCase()}] Database save failed, staying on contact page`);
    }
  };

  const isResultsStep = currentStep === totalSteps - 1;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        ref={modalContentRef}
        className="max-w-[95vw] md:max-w-4xl w-full max-h-[90vh] overflow-y-auto p-0 mx-auto z-[9999] flex flex-col"
        hideCloseButton
        style={{ zIndex: 9999 }}
      >
        <AssessmentModalHeader
          currentStep={currentStep}
          totalSteps={totalSteps}
          assessmentType={assessmentType}
          getProgress={getProgress}
          onClose={onClose}
          onConfirmClose={handleConfirmClose}
          currentCategory={currentStep >= 2 && currentStep < totalSteps - 2 ? categories[currentStep - 2] : undefined}
        />

        <div
          className={`p-3 md:p-6 relative py-0 px-0 mx-0 flex-1 flex flex-col ${
            isResultsStep ? 'justify-start' : 'justify-center'
          }`}
        >
          <AssessmentModalContent
            currentStep={currentStep}
            totalSteps={totalSteps}
            assessmentType={assessmentType}
            profileQuestions={profileQuestions}
            categories={categories}
            questionsByCategory={questionsByCategory}
            answers={answers}
            contactInfo={contactInfo}
            isSubmitting={isSubmitting}
            questionsData={data}
            onNext={nextStep}
            onAnswerChange={updateAnswer}
            onContactSubmit={handleContactSubmit}
            calculateScores={calculateScores}
            onClose={handleConfirmClose}
            getFilteredQuestionsForCurrentCategory={getFilteredQuestionsForCurrentCategory}
            axisDescriptions={axisDescriptions}
            onContactChange={handleContactChange}
          />
        </div>

        <AssessmentNavigation
          currentStep={currentStep}
          totalSteps={totalSteps}
          canProceed={canProceed()}
          onPrevious={prevStep}
          onNext={nextStep}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AssessmentModalGeneric;
